// /portal/requests — clients submit service requests; staff triage in
// the operator inbox at /app/requests.
//
// Insert: portal_insert policy enforces workspace + company match AND
// status='new'. The form sets created_via implicitly via the policy —
// the only allowed insert shape carries created_via_portal-equivalent
// (status=new). All later transitions (in_review, converted, declined)
// are staff-only.
//
// Read: portal_read policy gives the client their company's requests
// at any status so they know "yes the team saw this", "it was
// converted", etc.
//
// Storage note: the underlying column is `subject`, not `title` — kept
// from the original schema for forward-compatibility with a possible
// conversation integration. UI labels it "Title" for client clarity.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate, formatDate } from '@/lib/utils'
import { Inbox, Send, Loader2, AlertCircle, Clock, CheckCircle2, XCircle, Wrench } from 'lucide-react'
import { toast } from 'sonner'
import { PortalSkeletonList } from '@/pages/portal/PortalSkeleton'

interface RequestRow {
  id: string
  subject: string
  description: string | null
  status: 'new' | 'in_review' | 'converted' | 'declined' | string
  priority: 'normal' | 'urgent' | string
  location_id: string | null
  converted_job_id: string | null
  converted_at: string | null
  declined_reason: string | null
  created_at: string
}

interface LocationOption {
  id: string
  name: string
}

// Display keys can extend beyond raw request.status — see deriveDisplayKey:
// 'converted_complete' is rendered when the linked job has reached
// status='complete', so the request label tracks reality instead of
// freezing at "Scheduled" forever.
const STATUS_CFG: Record<string, { label: string; bg: string; color: string; border: string; icon: typeof Clock }> = {
  new:                { label: 'New',          bg: 'var(--info-dim)',     color: 'var(--info)',       border: 'var(--info-border)',     icon: Clock },
  in_review:          { label: 'In review',    bg: 'var(--accent-dim)',   color: 'var(--accent-text)', border: 'var(--accent-border)',  icon: Inbox },
  converted:          { label: 'Scheduled',    bg: 'var(--positive-dim)', color: 'var(--positive)',   border: 'var(--positive-border)', icon: CheckCircle2 },
  converted_complete: { label: 'Completed',    bg: 'var(--positive-dim)', color: 'var(--positive)',   border: 'var(--positive-border)', icon: Wrench },
  declined:           { label: 'Declined',     bg: 'var(--danger-dim)',   color: 'var(--danger)',     border: 'var(--danger-border)',   icon: XCircle },
}

export default function PortalRequests() {
  const { portalUser } = useAuth()

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['portal-requests', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return []
      const { data, error } = await supabase
        .from('portal_requests')
        .select('id, subject, description, status, priority, location_id, converted_job_id, converted_at, declined_reason, created_at')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as RequestRow[]
    },
    enabled: !!portalUser?.company_id,
  })

  // Follow the converted jobs' statuses so a request can move from
  // "Scheduled" → "Completed" as the underlying work progresses.
  // Single .in() query — collect ids and resolve client-side. A job
  // that's no longer visible (deleted, RLS revoked) falls back to the
  // raw 'Scheduled' label, never errors.
  const convertedJobIds = useMemo(
    () => Array.from(new Set(requests.map(r => r.converted_job_id).filter((id): id is string => !!id))),
    [requests],
  )
  const { data: convertedJobs = [] } = useQuery({
    queryKey: ['portal-requests-converted-jobs', convertedJobIds.join(',')],
    enabled: convertedJobIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_jobs')
        .select('id, status, scheduled_date')
        .in('id', convertedJobIds)
      if (error) throw error
      return (data ?? []) as Array<{ id: string; status: string; scheduled_date: string | null }>
    },
  })
  const jobStatusById = useMemo(() => {
    const m = new Map<string, { status: string; scheduled_date: string | null }>()
    for (const j of convertedJobs) m.set(j.id, { status: j.status, scheduled_date: j.scheduled_date })
    return m
  }, [convertedJobs])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <RequestForm />
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>
          Your requests
        </p>
        {isLoading ? (
          <PortalSkeletonList count={2} height={88} />
        ) : requests.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', background: 'var(--surface)', border: '1px dashed var(--border-default)', borderRadius: 10 }}>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Nothing here yet. Need something fixed? Submit your first request above — your team sees it immediately.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {requests.map(r => <RequestCard key={r.id} req={r} jobMeta={r.converted_job_id ? jobStatusById.get(r.converted_job_id) : undefined} />)}
          </div>
        )}
      </div>
    </div>
  )
}

function RequestCard({ req, jobMeta }: { req: RequestRow; jobMeta?: { status: string; scheduled_date: string | null } }) {
  // Display key: when the converted job has reached 'complete' the
  // request label and stamp track that fact; otherwise stick with the
  // raw request status.
  const isCompleted = req.status === 'converted' && jobMeta?.status === 'complete'
  const displayKey = isCompleted ? 'converted_complete' : req.status
  const cfg = STATUS_CFG[displayKey] ?? STATUS_CFG.new
  const Icon = cfg.icon
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${cfg.color}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
          <Icon style={{ width: 10, height: 10 }} />
          {cfg.label}
        </span>
        {req.priority === 'urgent' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', border: '1px solid var(--warning-border)' }}>
            <AlertCircle style={{ width: 10, height: 10 }} /> Urgent
          </span>
        )}
        <span style={{ fontSize: 10, color: 'var(--ink-tertiary)', marginLeft: 'auto' }}>
          {formatRelativeDate(req.created_at)}
        </span>
      </div>
      <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{req.subject}</p>
      {req.description && (
        <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '4px 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{req.description}</p>
      )}
      {req.status === 'converted' && (
        isCompleted && jobMeta?.scheduled_date ? (
          <p style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--positive)', margin: '8px 0 0' }}>
            <Wrench style={{ width: 11, height: 11 }} /> Completed on {formatDate(jobMeta.scheduled_date, 'MMM d, yyyy')}
          </p>
        ) : req.converted_at ? (
          <p style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--positive)', margin: '8px 0 0' }}>
            <Wrench style={{ width: 11, height: 11 }} /> Scheduled on {formatDate(req.converted_at, 'MMM d, yyyy')}
          </p>
        ) : null
      )}
      {req.status === 'declined' && req.declined_reason && (
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '8px 0 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: 'var(--ink-secondary)' }}>Reason: </span>{req.declined_reason}
        </p>
      )}
    </div>
  )
}

function RequestForm() {
  const { portalUser } = useAuth()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    subject: '', description: '', location_id: '', priority: 'normal' as 'normal' | 'urgent',
  })

  // Location picker source — only the client's own company's locations
  // are visible thanks to the existing portal_read on locations.
  const { data: locations = [] } = useQuery({
    queryKey: ['portal-locations-picker', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return []
      const { data, error } = await supabase.from('locations').select('id, name').eq('company_id', portalUser.company_id).order('name')
      if (error) throw error
      return (data ?? []) as LocationOption[]
    },
    enabled: !!portalUser?.company_id,
    staleTime: 60_000,
  })

  const contactQuery = useQuery({
    queryKey: ['portal-self-contact', portalUser?.contact_id],
    queryFn: async () => {
      if (!portalUser?.contact_id) return null
      const { data } = await supabase.from('contacts').select('first_name, last_name, email').eq('id', portalUser.contact_id).maybeSingle()
      return data
    },
    enabled: !!portalUser?.contact_id,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!portalUser) throw new Error('Portal session missing')
      const c = contactQuery.data
      const contactName = [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim() || 'Portal user'
      const { error } = await supabase.from('portal_requests').insert({
        workspace_id: portalUser.workspace_id,
        company_id: portalUser.company_id,
        contact_id: portalUser.contact_id,
        contact_name: contactName,
        contact_email: c?.email ?? null,
        subject: form.subject.trim(),
        description: form.description.trim() || null,
        location_id: form.location_id || null,
        priority: form.priority,
        status: 'new',
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Request submitted')
      setForm({ subject: '', description: '', location_id: '', priority: 'normal' })
      queryClient.invalidateQueries({ queryKey: ['portal-requests', portalUser?.company_id] })
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to submit'),
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.subject.trim()) { toast.error('Title is required'); return }
    if (!form.description.trim()) { toast.error('Description is required'); return }
    saveMutation.mutate()
  }

  return (
    <form onSubmit={onSubmit}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Submit a request</p>
      <FormField label="Title *">
        <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
          placeholder="Walk-in cooler not cooling" style={inp} />
      </FormField>
      <FormField label="Description *">
        <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          placeholder="What's happening? When did you notice?" rows={3}
          style={{ ...inp, resize: 'none', lineHeight: 1.5 }} />
      </FormField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <FormField label="Location (optional)">
          <select value={form.location_id} onChange={e => setForm(p => ({ ...p, location_id: e.target.value }))}
            style={{ ...inp, cursor: 'pointer' }}>
            <option value="">No specific location</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </FormField>
        <FormField label="Priority">
          <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value as 'normal' | 'urgent' }))}
            style={{ ...inp, cursor: 'pointer' }}>
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
          </select>
        </FormField>
      </div>
      <button type="submit" disabled={saveMutation.isPending}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
        {saveMutation.isPending ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 13, height: 13 }} />}
        {saveMutation.isPending ? 'Submitting…' : 'Submit request'}
      </button>
    </form>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 11px',
  background: 'var(--field-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)',
  outline: 'none', boxSizing: 'border-box',
}
